import {Alert, Button, Flex, Input, Modal, Table, TableColumnsType, Tooltip} from 'antd'
import {CopyOutlined} from '@ant-design/icons'
import {casinoKeyService, userService} from "../components/api";
import {useEffect, useState} from "react";
import {ErrorView} from "../components/show-error";
import {useSearchParams} from "react-router";
import {DataEntryParent} from "../components/data-entry-parent"
import {CasinoKeyState} from '../api/ga/config_manager/casinokey/casinokey_pb'
import {timestampView} from "../components/datetime";
import {calculateHMAC_SHA256} from "../components/hmac";

function casinoKeyStateText(v: CasinoKeyState): string {
  switch (v) {
    case CasinoKeyState.UNKNOWN:                  return '-'
    case CasinoKeyState.ACTIVATED:                return 'active'
    case CasinoKeyState.READY_FOR_APPLYING_NEW_KEY:
    case CasinoKeyState.APPLYING_NEW_KEY:         return 'process...'
    case CasinoKeyState.KEY_DEPLOYMENT:           return 'ready to remove old key'
    case CasinoKeyState.READY_FOR_ROLLING_OUT:
    case CasinoKeyState.ROLLING_OUT:              return 'process...'
  }
}

interface DataType {
  key: string
  id: string
  title: string
  jurisdiction: string
  keyState?: CasinoKeyState
  keyObfuscated?: string
  lastActionBy?: string
  updatedAt?: string
}

function GenerateModal({ casino, onClose }: { casino: DataType; onClose: () => void }) {
  const [modal, contextHolder] = Modal.useModal()
  const [generatedKey, setGeneratedKey] = useState<string | null>(casino.keyObfuscated ?? null)
  const [copied, setCopied] = useState(false)

  const copyKey = () => {
    if (!generatedKey) return
    navigator.clipboard.writeText(generatedKey)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const run = () => {
    casinoKeyService.generate({ casinoId: casino.id })
      .then((resp) => setGeneratedKey(resp.key))
      .catch((e: any) => { ErrorView(modal, 'generate error', e) })
  }

  const rollout = () => {
    casinoKeyService.rollout({ casinoId: casino.id })
      .catch((e: any) => { ErrorView(modal, 'rollout error', e) })
  }

  return (
    <Modal title={`Switch keys for ${casino.id}`} open onCancel={onClose} footer={null}>
      <Flex vertical gap={8}>
        <Input value={casino.keyState !== undefined ? casinoKeyStateText(casino.keyState) : '—'} readOnly addonBefore={<span style={{ display: 'inline-block', width: 40, textAlign: 'left' }}>state</span>} />
        {generatedKey !== null && (
          <Input value={generatedKey} readOnly addonBefore={<span style={{ display: 'inline-block', width: 40, textAlign: 'left' }}>key</span>} addonAfter={!generatedKey.includes('*') && <Tooltip title="Copied!" open={copied}><CopyOutlined onClick={copyKey} /></Tooltip>} />
        )}
        <Flex gap={8} justify="space-between">
          <Flex gap={8}>
            <Button
              type={casino.keyState === CasinoKeyState.ACTIVATED ? 'primary' : 'default'}
              onClick={() => modal.confirm({
                title: 'Generate new key?',
                content: <><p>Casino: <b>{casino.id}</b></p><p>This will replace the <b>primary key</b>. The casino may become temporarily unavailable. The secondary key is not affected if it is in use.</p></>,
                onOk: run,
              })}
            >Generate new key</Button>
            {casino.keyState === CasinoKeyState.KEY_DEPLOYMENT && (
              <Button
              type="primary"
              onClick={() => modal.confirm({
                title: 'Remove old key?',
                content: `Casino: ${casino.id}`,
                onOk: rollout,
              })}
            >Remove old key</Button>
            )}
          </Flex>
          <Button onClick={onClose}>Close</Button>
        </Flex>
      </Flex>
      {contextHolder}
    </Modal>
  )
}

function VerifyModal({ casino, onClose }: { casino: DataType; onClose: () => void }) {
  const [modal, contextHolder] = Modal.useModal()

  const verifyPayload = `{"casinoId":"${casino.id}"}`
  const [sign, setSign] = useState('')

  const [helpOpen, setHelpOpen] = useState(false)
  const [helpKey, setHelpKey] = useState('')

  const calcSign = () => {
    calculateHMAC_SHA256(helpKey, verifyPayload)
      .then((hash: string) => {
        setSign(hash)
        setHelpOpen(false)
      })
      .catch((e: any) => { ErrorView(modal, 'calculate sign error', e) })
  }

  const run = () => {
    casinoKeyService.verify({ casinoId: casino.id }, { headers: { 'X-REQUEST-SIGN': sign } })
      .then(() => {
        modal.success({
          title: 'Verified',
          content: `Casino ${casino.id} key verified successfully.`,
        })
      })
      .catch((e: any) => { ErrorView(modal, 'verify error', e) })
  }

  return (
    <Modal title={`Verify key for ${casino.id}`} open onCancel={onClose} footer={null}>
      <Flex vertical gap={8}>
        <Input value={verifyPayload} readOnly addonBefore={<span style={{ display: 'inline-block', width: 32, textAlign: 'left' }}>body</span>} addonAfter={<CopyOutlined onClick={() => navigator.clipboard.writeText(verifyPayload)} />} />
        <Input value={sign} onChange={(e) => setSign(e.target.value)} addonBefore={<span style={{ display: 'inline-block', width: 32, textAlign: 'left' }}>sign</span>} placeholder="sign" />
        <Flex gap={8} justify="space-between">
          <Flex gap={8}>
            <Button type="primary" onClick={run}>Verify</Button>
            <Button onClick={() => setHelpOpen(true)}>Help</Button>
          </Flex>
          <Button onClick={onClose}>Close</Button>
        </Flex>
      </Flex>
      <Modal title="Calculate sign" open={helpOpen} onCancel={() => setHelpOpen(false)} onOk={calcSign} okText="Calculate">
        <Flex vertical gap={8}>
          <Input value={verifyPayload} readOnly addonBefore={<span style={{ display: 'inline-block', width: 32, textAlign: 'left' }}>body</span>} />
          <Input value={helpKey} onChange={(e) => setHelpKey(e.target.value)} addonBefore={<span style={{ display: 'inline-block', width: 32, textAlign: 'left' }}>key</span>} placeholder="key" />
        </Flex>
      </Modal>
      {contextHolder}
    </Modal>
  )
}

export default function CasinoKeys() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [tableSearch, setTableSearch] = useState(searchParams.get('search') ?? '')

  const [modal, contextHolder] = Modal.useModal()

  const [casinos, setCasinos] = useState([] as DataType[])
  const [streamError, setStreamError] = useState(false)

  const [generateCasino, setGenerateCasino] = useState<DataType | null>(null)
  const [verifyCasino, setVerifyCasino] = useState<DataType | null>(null)

  useEffect(() => {
    const ac = new AbortController()

    ;(async () => {
      while (!ac.signal.aborted) {
        try {
          let initialized = false
          for await (const meta of userService.metadataStream({}, { signal: ac.signal })) {
            if (!initialized) {
              initialized = true
              casinoKeyService.listCasinos({})
                .then((resp) => {
                  setCasinos((resp.casinos ?? []).map((c) => ({
                    key: c.casinoId,
                    id: c.casinoId,
                    title: c.title,
                    jurisdiction: c.jurisdiction,
                    keyState: c.state,
                    keyObfuscated: c.keyObfuscated,
                    lastActionBy: c.lastActionBy,
                    updatedAt: timestampView(c.updatedAt),
                  })))
                })
                .catch((e: any) => { ErrorView(modal, 'load error', e) })
            } else {
              if (meta.casinoKeyInfo) {
                const info = meta.casinoKeyInfo
                setCasinos(prev => prev.map(c => c.id === info.casinoId
                  ? {...c,
                    keyState: info.state,
                    keyObfuscated: info.keyObfuscated,
                    lastActionBy: info.lastActionBy,
                    updatedAt: timestampView(info.updatedAt),
                  }
                  : c
                ))
              }
            }
          }
        } catch (e: any) {
          if (ac.signal.aborted) break
          setStreamError(true)
          await new Promise(resolve => setTimeout(resolve, 1000))
        }
        setStreamError(false)
      }
    })()

    return () => ac.abort()
  }, [])

  const columns: TableColumnsType<DataType> = [
    {
      title: 'id',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'title',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'jurisdiction',
      dataIndex: 'jurisdiction',
      key: 'jurisdiction',
    },
    {
      title: 'key state',
      dataIndex: 'keyState',
      key: 'keyState',
      render: (v?: CasinoKeyState) => v !== undefined ? casinoKeyStateText(v) : '—',
    },
    {
      title: 'last action by',
      dataIndex: 'lastActionBy',
      key: 'lastActionBy',
      render: (v?: string) => v || '—',
    },
    {
      title: 'updated at',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
    },
    {
      title: 'actions',
      key: 'actions',
      render: (_: any, row: DataType) => (
        <Flex gap={4}>
          <Button size="small" onClick={() => setGenerateCasino(row)}>Switch keys</Button>
          <Button size="small" onClick={() => setVerifyCasino(row)}>Verify key</Button>
        </Flex>
      ),
    },
  ]

  const filteredCasinos = tableSearch
    ? casinos.filter((r) => JSON.stringify(r).toLowerCase().includes(tableSearch.toLowerCase()))
    : casinos

  return (
    <>
      <div style={{maxWidth: 1400, margin: '0 auto', padding: '24px 24px 40px', textAlign: 'left'}}>
      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 0}}>
        <h1 style={{fontSize: 32, fontWeight: 900, color: 'var(--type-primary)', margin: 0}}>
          Casino Keys
        </h1>
      </div>
      <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 16, marginBottom: 16, alignItems: 'start'}}>
        <DataEntryParent text="search">
          <Input.Search
            placeholder="search"
            value={tableSearch}
            allowClear
            onChange={(e) => {
              const v = e.target.value
              setTableSearch(v)
              setSearchParams((p) => { if (v) p.set('search', v); else p.delete('search'); return p })
            }}
          />
        </DataEntryParent>
        {streamError && <Alert type="warning" message="Connection lost, reconnecting…" showIcon banner />}
      </div>
      <div style={{background: 'var(--surface-white)', borderRadius: 'var(--radius-basic)', border: '1px solid var(--border)', overflow: 'hidden'}}>
      <Table
        dataSource={filteredCasinos}
        columns={columns}
        size={'small'}
        pagination={{ defaultPageSize: 100 }}
      />
      </div>
      </div>
      {generateCasino && <GenerateModal casino={casinos.find(c => c.id === generateCasino.id) ?? generateCasino} onClose={() => setGenerateCasino(null)} />}
      {verifyCasino && <VerifyModal casino={verifyCasino} onClose={() => setVerifyCasino(null)} />}
      {contextHolder}
    </>
  )
}